import {DetailsField, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {useTranslation} from '@/translations/i18n'
import {ProjectDetailsInfoEditDialog} from '../project-details-info-edit-dialog/ProjectDetailsInfoEditDialog'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '@/features/projects/types'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {Text} from '@/components/ui/text/Text'
import {formatLocaleDate} from '@/dayjs'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ClockRewindIcon} from '@/components/ui/icon/Icon'
import {
    assistiveRegimeToRehabCareCommitment,
    projectAssistiveRegimeToLabel,
    projectSectorToLabel,
    projectTypeToLabel,
    calculateTotalAccesses,
    projectCycleCountToLabel
} from '../../utils'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {WarningLabel} from '@/components/ui/warning-label/WarningLabel'
import {useGetPlannedAccesses} from '@/features/planned-accesses/queries/useGetPlannedAccesses'
import {useMemo} from 'react'
import dayjs from 'dayjs'

export const ProjectDetailsInfoSection = () => {
    const {t} = useTranslation()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()
    const {data: plannedAccessesData} = useGetPlannedAccesses({
        urlParams: {
            projectId: project.id
        },
        options: {
            enabled: project.sector == 'mixed'
        }
    })

    const fm22Sector = useMemo(() => {
        if (!plannedAccessesData?.plannedAccesses || !project.startDate || !project.expirationDate) {
            return null
        }

        const duration = dayjs(project.expirationDate).diff(dayjs(project.startDate), 'day')

        // Calculate total accesses for each planned access based on frequency and duration
        const accessesWithTotals = plannedAccessesData.plannedAccesses
            .filter(access => access.frequency) // Only consider accesses with frequency (weekly/monthly)
            .map(access => ({
                accessType: access.accessType,
                sector: access.sector,
                totalAccesses: calculateTotalAccesses(duration, access.frequency!, access.numberOfAccesses)
            }))

        if (accessesWithTotals.length === 0) {
            return null
        }

        // Find the access with the highest total accesses
        // If two accesses have the same total, default to 'outpatient'
        const accessWithHighestTotal = accessesWithTotals.reduce((max, current) => {
            if (current.totalAccesses > max.totalAccesses) {
                return current
            } else if (current.totalAccesses === max.totalAccesses) {
                // If totals are equal, prefer 'outpatient' over 'domiciliary'
                return current.sector === 'outpatient' ? current : max
            }
            return max
        })

        return accessWithHighestTotal.sector as 'outpatient' | 'domiciliary' | null
    }, [plannedAccessesData, project.startDate, project.expirationDate])

    return (
        <DetailsFieldsSection
            variant="primary"
            title={t('projects:project_details')}
            headingEndSlot={isEditable && <ProjectDetailsInfoEditDialog project={project} />}
        >
            <Grid columns={2} gap={3} fullWidth>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:project_details_start_date')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {project.startDate ? formatLocaleDate(project.startDate, 'DD/MM/YYYY') : '-'}
                    </Text>
                </DetailsField>
                <Grid columns={2} gap={3} fullWidth>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:project_details_end_date')}
                        </Text>
                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                            {project.expirationDate ? (
                                formatLocaleDate(project.expirationDate, 'DD/MM/YYYY')
                            ) : (
                                <WarningLabel text={t('users:to_compile')} />
                            )}
                        </Text>
                    </DetailsField>
                    <DetailsField fullWidth>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('projects:project_details_duration')}
                        </Text>

                        {project.durationDays ? (
                            project.actualDays ? (
                                <Tooltip
                                    content={t('projects:count_working_days', {count: project.actualDays || 0})}
                                    variant="light"
                                >
                                    <Flexbox gap={2} align="center">
                                        <ClockRewindIcon size={16} />
                                        <Text
                                            palette="neutral"
                                            shade="950"
                                            size="textSm"
                                            weight="500"
                                            style={{textDecoration: 'underline'}}
                                        >
                                            {t('commons:days_count', {count: project.durationDays})}
                                        </Text>
                                    </Flexbox>
                                </Tooltip>
                            ) : (
                                <Flexbox gap={2} align="center">
                                    <ClockRewindIcon size={16} />
                                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                                        {t('commons:days_count', {count: project.durationDays})}
                                    </Text>
                                </Flexbox>
                            )
                        ) : (
                            <Text palette="neutral" shade="950" size="textSm" weight="500">
                                -
                            </Text>
                        )}
                    </DetailsField>
                </Grid>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('commons:project_type')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {project.type ? (
                            <Flexbox render="span" gap={1} align="center">
                                {t(projectTypeToLabel[project.type])}
                                {project.type == 'cyclic' && project.numberOfCycles && (
                                    <span>({t(projectCycleCountToLabel[project.numberOfCycles])})</span>
                                )}
                            </Flexbox>
                        ) : (
                            <WarningLabel text={t('users:to_compile')} />
                        )}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('commons:intervention_mode')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {project.sector ? (
                            project.sector == 'mixed' && fm22Sector ? (
                                <>
                                    <Flexbox render="span" gap={1} align="center">
                                        <Text palette="neutral" shade="950" size="textSm" weight="500">
                                            {t(projectSectorToLabel[fm22Sector])}
                                        </Text>
                                        -
                                        <Text palette="neutral" shade="950" size="textSm" weight="400">
                                            {t(projectSectorToLabel[project.sector])}
                                        </Text>
                                    </Flexbox>
                                </>
                            ) : (
                                t(projectSectorToLabel[project.sector])
                            )
                        ) : (
                            <WarningLabel text={t('users:to_compile')} />
                        )}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:project_details_intervention_commitment')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {project.sector == 'domiciliary' ? (
                            t('projects:not_applicable')
                        ) : project.assistiveRegime ? (
                            t('commons:duration_minutes', {
                                value: assistiveRegimeToRehabCareCommitment[project.assistiveRegime]
                            })
                        ) : (
                            <WarningLabel text={t('users:to_compile')} />
                        )}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:project_details_assistance')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {t('projects:project_details_assistance_non_residential')}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:project_details_sub_assistance')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {project.assistiveRegime ? t(projectAssistiveRegimeToLabel[project.assistiveRegime]) : '-'}
                    </Text>
                </DetailsField>
            </Grid>
        </DetailsFieldsSection>
    )
}
